// components/teams/detailCards/modals/BoltOnPurchaseModal.jsx
import React, { useState, useMemo, useEffect } from 'react';
import ReactDOM from 'react-dom';
import SubmitButton from '../../../forms/formComponents/SubmitButton';
import ErrorMessage from '../../../forms/formComponents/ErrorMessage';
import { functions, auth, db } from '../../../../firebase-client';
import { httpsCallable } from 'firebase/functions';
import { onAuthStateChanged } from 'firebase/auth';
import { useAuth } from '../../../../auth/AuthContext';
import { doc, getDoc } from 'firebase/firestore';
import { getCurrencyMeta } from '../../../../utils/currency';
import { getStripeFeeConfig, grossUpForStripeFeeMinor } from '../../../../utils/fees';
import { getBoltOnMembershipYearWindow } from '../../../../utils/membershipYear';

/** Helpers — adjust if your Stripe pricing differs */
const estimateStripeFeeMinor = (amountMinor, currency) => {
  const cfg = getStripeFeeConfig({
    currency
  });
  const pct = Number(cfg.percent || 0);
  const fixed = Number(cfg.fixedMinor || 0);
  return Math.max(0, Math.round(amountMinor * pct + fixed));
};
export default function BoltOnPurchaseModal({
  teamId,
  boltOn,
  onClose
}) {
  const {
    authLoading
  } = useAuth();
  const [mode, setMode] = useState('manual');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [currency, setCurrency] = useState('GBP');
  const [teamSetup, setTeamSetup] = useState({});
  const [availableCreditMinor, setAvailableCreditMinor] = useState(0);
  const quantityLimit = Number.isFinite(Number(boltOn?.checkoutQuantityLimit)) && Number(boltOn?.checkoutQuantityLimit) > 0 ? Math.floor(Number(boltOn.checkoutQuantityLimit)) : null;
  const totalLimit = Number.isFinite(Number(boltOn?.totalQuantityLimit)) && Number(boltOn?.totalQuantityLimit) > 0 ? Math.floor(Number(boltOn.totalQuantityLimit)) : null;
  const [quantity, setQuantity] = useState(1);
  useEffect(() => {
    if (!teamId) return;
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'teams', teamId, 'stripe', 'setup'));
        if (snap.exists()) {
          const setup = snap.data() || {};
          setCurrency(setup.currency || 'GBP');
          setTeamSetup(setup);
        }
      } catch (e) {}
    })();
  }, [teamId]);
  useEffect(() => {
    const uid = auth.currentUser?.uid;
    if (!teamId || !boltOn?.id || !uid || authLoading) return;
    getDoc(doc(db, 'teams', teamId, 'members', uid, 'boltOns', boltOn.id))
      .then((snapshot) => setAvailableCreditMinor(Math.max(0, Number(snapshot.data()?.membershipYearCreditMinor || 0))))
      .catch(() => setAvailableCreditMinor(0));
  }, [authLoading, boltOn?.id, teamId]);
  useEffect(() => {
    setQuantity(prev => {
      const next = Math.max(1, Number(prev) || 1);
      return quantityLimit ? Math.min(next, quantityLimit) : next;
    });
  }, [quantityLimit, boltOn?.id]);

  /** Ensure fresh auth token before calling callable functions */
  const requireFreshAuth = async () => {
    if (auth.currentUser) {
      await auth.currentUser.getIdToken(true);
      return auth.currentUser;
    }
    const u = await new Promise((resolve, reject) => {
      const timeout = setTimeout(() => resolve(auth.currentUser || null), 5000);
      const unsub = onAuthStateChanged(auth, cu => {
        clearTimeout(timeout);
        unsub();
        resolve(cu);
      }, err => {
        clearTimeout(timeout);
        unsub();
        reject(err);
      });
    });
    if (!u) throw new Error('You must be signed in.');
    await u.getIdToken(true);
    return u;
  };
  const callWithAuth = async (name, payload) => {
    await requireFreshAuth();
    const fn = httpsCallable(functions, name);
    return fn(payload);
  };
  const doManualCheckout = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const {
        data
      } = await callWithAuth('createBoltOnCheckoutSession', {
        teamId,
        boltOnId: boltOn.id,
        quantity
      });
      if (data?.completed) {
        onClose?.();
        return;
      }
      if (!data?.url) throw new Error('No checkout URL returned.');
      window.location.href = data.url;
    } catch (e) {
      setError(e?.message || 'Failed to start checkout.');
      setSubmitting(false);
    }
  };
  const doSubscription = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const {
        data
      } = await callWithAuth('createBoltOnSubscription', {
        teamId,
        boltOnId: boltOn.id,
        quantity
      });
      if (!data?.url) throw new Error('No checkout URL returned.');
      window.location.href = data.url;
    } catch (e) {
      setError(e?.message || 'Failed to start subscription.');
      setSubmitting(false);
    }
  };
  const onSubmit = e => {
    e.preventDefault();
    if (mode === 'manual') return doManualCheckout();
    return doSubscription();
  };

  // Disable actions until auth is ready
  const actionsDisabled = useMemo(() => {
    return submitting || authLoading || !auth.currentUser;
  }, [submitting, authLoading]);

  // ------- Pricing preview -------
  const unitAmountPence = Math.max(0, Number(boltOn?.amount ?? 0)); // stored in pence
  const subtotalPence = unitAmountPence * Math.max(1, Number(quantity) || 1);
  const membershipWindow = getBoltOnMembershipYearWindow(new Date(), teamSetup, boltOn || {});
  const dueBeforeCreditPence = membershipWindow.enabled
    ? Math.round(subtotalPence * membershipWindow.prorationFactor)
    : subtotalPence;
  const creditAppliedPence = Math.min(availableCreditMinor, dueBeforeCreditPence);
  const dueBasePence = Math.max(0, dueBeforeCreditPence - creditAppliedPence);
  const grossAmountPence = grossUpForStripeFeeMinor(dueBasePence, getStripeFeeConfig({ currency }));
  const feeMode = boltOn?.stripeFeeHandling === 'add' ? 'add' : 'deduct'; // default to 'deduct' for old data

  const feePence = feeMode === 'add' ? Math.max(0, grossAmountPence - dueBasePence) : estimateStripeFeeMinor(dueBasePence, currency);
  const memberPays = feeMode === 'add' ? grossAmountPence : dueBasePence;
  const renewalTooCloseForSubscription = membershipWindow.enabled && memberPays > 0
    && membershipWindow.nextYearStart.getTime() < Date.now() + 2 * 86400000 + 60000;
  const renewalPays = feeMode === 'add' && subtotalPence > 0 ? grossUpForStripeFeeMinor(subtotalPence, getStripeFeeConfig({ currency })) : subtotalPence;
  const coverageEnd = membershipWindow.enabled ? membershipWindow.nextYearStart : new Date();
  if (!membershipWindow.enabled) {
    if (boltOn.renewalUnit === 'month') coverageEnd.setUTCMonth(coverageEnd.getUTCMonth() + Number(boltOn.renewalEvery || 1));
    else coverageEnd.setUTCFullYear(coverageEnd.getUTCFullYear() + Number(boltOn.renewalEvery || 1));
  }
  const clubReceives = feeMode === 'deduct' ? dueBasePence - feePence : dueBasePence;
  const periodLabel = `Every ${boltOn.renewalEvery} ${boltOn.renewalUnit}${boltOn.renewalEvery > 1 ? 's' : ''}`;
  const {
    symbol,
    decimals
  } = getCurrencyMeta(currency);
  const fmt = minor => `${symbol}${(Number(minor || 0) / Math.pow(10, decimals)).toFixed(decimals)}`;
  const handleQuantityChange = e => {
    const raw = e.target.value;
    const parsed = parseInt(raw || '1', 10);
    if (!Number.isFinite(parsed) || parsed < 1) {
      setQuantity(1);
      return;
    }
    setQuantity(quantityLimit ? Math.min(parsed, quantityLimit) : parsed);
  };
  return ReactDOM.createPortal(<div className="modal-backdrop">
      <div className="modal-content">
        <h2 className="modal-title">Get {boltOn.name}</h2>

        {error && <ErrorMessage message={error} />}

        {(authLoading || !auth.currentUser) && !error && <p className="text-muted" style={{
        marginTop: 8
      }}>
            Please sign in (or wait a moment) to continue.
          </p>}

        {boltOn?.description && <p className="text-muted">{boltOn.description}</p>}

        <p>
          <strong>Price:</strong> {fmt(unitAmountPence)} each ({periodLabel})
        </p>

        {/* Fee breakdown preview */}
        <div className="form-hint" style={{
        marginTop: 8
      }}>
          <div><strong>Quantity:</strong> {quantity}</div>
          <div><strong>Subtotal:</strong> {fmt(subtotalPence)}</div>
          {membershipWindow.enabled && <>
            <div><strong>Initial period before fees:</strong> {fmt(dueBasePence)}{membershipWindow.prorationFactor < 1 ? ' (pro-rata)' : ''}</div>
          </>}
          <div><strong>This payment covers:</strong> today to {coverageEnd.toLocaleDateString('en-GB')}, once payment completes.</div>
          <div><strong>Next renewal:</strong> {fmt(renewalPays)} on {coverageEnd.toLocaleDateString('en-GB')}{feeMode === 'add' ? ' (includes estimated Stripe fee)' : ''}.</div>
          {creditAppliedPence > 0 && <div><strong>Alignment credit:</strong> −{fmt(creditAppliedPence)}</div>}
          <div><strong>Estimated Stripe fee:</strong> {fmt(feePence)}</div>
          <div><strong>You pay now:</strong> {fmt(memberPays)} {feeMode === 'add' && <span className="text-muted"> (includes estimated Stripe fee)</span>}</div>
          <div><strong>Club receives:</strong> {fmt(clubReceives)} {feeMode === 'deduct' && <span className="text-muted"> (after estimated Stripe fee)</span>}</div>
          {mode === 'manual' && <small className="text-muted">You pay each renewal separately; future payments are not taken automatically.</small>}
          {mode === 'subscription' && <small className="text-muted" style={{
          display: 'block',
          marginTop: 6
        }}>
              After the initial payment, {fmt(renewalPays)} will be charged automatically each renewal ({periodLabel}), starting on {coverageEnd.toLocaleDateString('en-GB')}. Fees are estimates and can vary by card/wallet.
            </small>}
        </div>

        <form onSubmit={onSubmit} className="modal-form space-y-3" style={{
        marginTop: 12
      }}>
          <div className="form-group">
            <label>Quantity</label>
            <input type="number" min="1" step="1" max={quantityLimit || undefined} value={quantity} onChange={handleQuantityChange} className="input" disabled={actionsDisabled} />
            <small className="text-muted">
              {quantityLimit ? `Maximum ${quantityLimit} per checkout.` : totalLimit ? `Overall limit: ${totalLimit}.` : 'No checkout maximum has been set for this bolt-on.'}
            </small>
          </div>

          <div className="form-group">
            <label>Payment mode</label>
            <select value={mode} onChange={e => setMode(e.target.value)} className="input" disabled={actionsDisabled}>
              <option value="manual">Manual (one period upfront)</option>
              <option value="subscription" disabled={renewalTooCloseForSubscription}>Subscription (recurring)</option>
            </select>
            {renewalTooCloseForSubscription && <small className="text-muted">Automatic renewal setup is unavailable within two days of the club renewal date. Pay manually for this period, then set up automatic renewals after that date.</small>}
          </div>

          <div className="form-actions">
            <SubmitButton type="button" variant="secondary" label="Cancel" onClick={onClose} disabled={submitting} />
            <SubmitButton type="submit" variant="primary" disabled={actionsDisabled} label={submitting ? 'Processing…' : 'Continue to pay'} />
          </div>
        </form>
      </div>
    </div>, document.body);
}
